import { GetAllDriverFixActions } from './GraphQL';
import {
  filterEventsByDate,
  previousStockingDate,
} from '../utils/stockingWindow';

// Shared driver-action fetch pipeline. Every consumer needs the same three
// things — drain each event type's pagination, cover the stocking date AND the
// previous calendar day (the stocking window straddles midnight), then filter to
// the window — and differs only in how it projects the result. That shape lives
// here; the projections stay with their callers (`libs/stopStatus` buckets by
// family, `hooks/useDriverLocationsPoll` maps scans by locationId,
// `ViewStockingDashboard` pulls out locationIds).

// The two events that tell a stop's on-site story: the driver scanning in
// on arrival, and the completed stocking. Named here so the hook that
// fetches them and the composer that reads them agree on the strings.
export const ARRIVAL_SCAN_EVENT = 'PackingSlipQuery';
export const LOCATION_STOCKED_EVENT = 'LocationStocked';

// Event types the dashboards care about.
export const DRIVER_ACTION_EVENT_TYPES = [
  LOCATION_STOCKED_EVENT,
  'DropOffComplete',
  ARRIVAL_SCAN_EVENT,
  'LocationUnreachable',
  'VanTemperature',
  'InventoryRemoval',
  'ClearOutDecommission',
];

/**
 * Fetch driver-action events for a stocking day, window-filtered.
 *
 * `tolerateFailure` (default) keeps a bad query from blanking out the rest of the
 * board: the failing (event type, date) slice contributes whatever pages it
 * drained and its event type is reported in `failedEventTypes`, so a multi-type
 * board can render what it has and warn that the data is partial. Pass
 * `tolerateFailure: false` when the caller has a better fallback than partial
 * data — Monitoring's scan cache, for instance, would rather keep the previous
 * good map than cache an empty one.
 *
 * @param {string[]} eventTypes  driver-action event types to fetch
 * @param {string} stockingDate  "YYYY-MM-DD"
 * @param {object} [options]
 * @param {boolean} [options.tolerateFailure=true]
 * @returns {Promise<{
 *   eventsByType: Object<string, Array>,  // window-filtered, both dates merged
 *   failedEventTypes: string[],           // unique, empty when everything succeeded
 * }>}
 * @throws when `tolerateFailure` is false and any slice fails
 */
export async function fetchDriverActionEvents(
  eventTypes,
  stockingDate,
  { tolerateFailure = true } = {},
) {
  const previousDate = previousStockingDate(stockingDate);
  const failed = new Set();

  const fetchSlice = (eventType, date) =>
    GetAllDriverFixActions(
      date,
      eventType,
      tolerateFailure
        ? {
            onError: (err) => {
              console.error(
                `[driverActions] fetch failed for ${eventType} on ${date}:`,
                err,
              );
              failed.add(eventType);
            },
          }
        : undefined,
    );

  // 2 dates × N event types, all in flight together.
  const results = await Promise.all(
    eventTypes.map(async (eventType) => {
      const [today, prev] = await Promise.all([
        fetchSlice(eventType, stockingDate),
        fetchSlice(eventType, previousDate),
      ]);
      return filterEventsByDate([...today, ...prev], stockingDate);
    }),
  );

  const eventsByType = {};
  eventTypes.forEach((eventType, i) => {
    eventsByType[eventType] = results[i];
  });
  return { eventsByType, failedEventTypes: Array.from(failed) };
}
